import { useEffect, useState } from "react";
import { api } from "../lib/api";

type Overview = {
  merchant: { id: string; name: string };
  customers_total: number;
  customers_active_30d: number;
  punches_total: number;
  redemptions_total: number;
  redemption_conversion: number; // 0..1
  avg_punches_per_customer: number;
  punches_by_day: { day: string; n: number }[];
};

function Kpi({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {hint && <div className="mt-1 text-xs text-neutral-500">{hint}</div>}
    </div>
  );
}

function Sparkline({ data }: { data: { day: string; n: number }[] }) {
  if (data.length === 0) return null;
  const max = Math.max(1, ...data.map((d) => d.n));
  const w = 280, h = 60, pad = 4;
  const stepX = (w - pad * 2) / Math.max(1, data.length - 1);
  const pts = data.map((d, i) => {
    const x = pad + i * stepX;
    const y = h - pad - (d.n / max) * (h - pad * 2);
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full max-w-xs">
      <polyline fill="none" stroke="#138a3f" strokeWidth="2" points={pts} />
      {data.map((d, i) => {
        const x = pad + i * stepX;
        const y = h - pad - (d.n / max) * (h - pad * 2);
        return <circle key={i} cx={x} cy={y} r="2" fill="#138a3f" />;
      })}
    </svg>
  );
}

export function Dashboard() {
  const [data, setData] = useState<Overview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    api<Overview>("/api/overview").then(setData).catch((e: Error) => setErr(e.message));
  }, []);
  if (err) return <div className="text-red-600">{err}</div>;
  if (!data) return <div className="text-neutral-500">Loading…</div>;
  const pct = Math.round(data.redemption_conversion * 100);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{data.merchant.name}</h1>
        <p className="text-sm text-neutral-500">Read-only overview · refreshed just now</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Kpi label="Customers" value={data.customers_total} hint={`${data.customers_active_30d} active in last 30 d`} />
        <Kpi label="Punches" value={data.punches_total} hint={`avg ${data.avg_punches_per_customer.toFixed(1)} per customer`} />
        <Kpi label="Redemptions" value={data.redemptions_total} hint={`${pct}% conversion`} />
      </div>
      <div className="bg-white border border-neutral-200 rounded-lg p-4">
        <div className="text-xs uppercase tracking-wide text-neutral-500">Punches · last 7 days</div>
        <div className="mt-2"><Sparkline data={data.punches_by_day} /></div>
      </div>
    </div>
  );
}
